import { useRef, useState } from 'react';
import { apps } from '../../../mocks/profiles';

type AppType = typeof apps[0];
type ReviewType = AppType['reviews'][0];

function ReviewCarousel({ reviews }: { reviews: ReviewType[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const itemWidth = scrollRef.current.offsetWidth;
      const index = Math.round(scrollLeft / itemWidth);
      setActiveIndex(index);
    }
  };

  return (
    <div className="mx-5 mb-5">
      <p className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-1.5">
        <i className="ri-chat-quote-fill text-rose-400 text-sm"></i>
        ユーザーの口コミ
      </p>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory gap-2.5 pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {reviews.map((review, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-full snap-center bg-gray-50 rounded-xl p-4"
          >
            {/* ユーザー情報と★ */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-100">
                <i className="ri-user-smile-fill text-rose-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-700">
                  {review.userName}さん（{review.age}歳）
                </p>
                <div className="flex items-center gap-0.5">
                  {[...Array(review.rating)].map((_, j) => (
                    <i
                      key={j}
                      className="ri-star-fill text-amber-400 text-[10px]"
                    ></i>
                  ))}
                </div>
              </div>
            </div>

            {/* 女性画像 */}
            {review.reviewImage && (
              <div className="w-full h-36 rounded-lg overflow-hidden mb-3">
                <img
                  src={review.reviewImage}
                  alt="口コミ画像"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}

            {/* コメント */}
            <p className="text-gray-600 text-xs leading-relaxed italic">
              &ldquo;{review.comment}&rdquo;
            </p>
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-1.5 mt-2">
        {reviews.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${
              i === activeIndex ? 'w-4 bg-rose-400' : 'w-1.5 bg-gray-300'
            }`}
          ></div>
        ))}
      </div>
    </div>
  );
}

function AppCard({ app }: { app: AppType }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
      {/* ロゴ＆ヘッダー */}
      <div className={`${app.logoBg} px-5 py-6 flex items-center gap-4`}>
        <div className="w-16 h-16 flex-shrink-0 rounded-2xl overflow-hidden bg-white shadow-md">
          <img
            src={app.logoImage}
            alt={`${app.name} ロゴ`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-800">{app.name}</h3>
          <p className="text-gray-500 text-xs mt-0.5">{app.tagline}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i
                  key={i}
                  className={`ri-star-fill text-xs ${
                    i < Math.floor(app.rating) ? 'text-amber-400' : 'text-gray-200'
                  }`}
                ></i>
              ))}
            </div>
            <span className="text-xs font-bold text-gray-700">{app.rating}</span>
            <span className="text-xs text-gray-400">|</span>
            <span className="text-xs text-gray-400">{app.downloads}DL</span>
          </div>
        </div>
      </div>

      {/* おすすめポイント */}
      <div className="mx-5 mt-5">
        <div className="relative bg-gradient-to-r from-rose-500 to-pink-500 rounded-xl px-4 py-3 text-center shadow-md">
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
            <i className="ri-fire-fill mr-0.5"></i>イチオシ
          </div>
          <p className="text-white font-bold text-sm mt-1">
            <i className="ri-star-fill text-amber-300 mr-1"></i>
            {app.recommendPoint}
          </p>
        </div>
      </div>

      {/* セクシー画像 */}
      <div className="mx-5 mt-4">
        <div className="w-full h-56 rounded-xl overflow-hidden">
          <img
            src={app.recommendImage}
            alt={`${app.name} イメージ`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* 特徴 */}
      <div className="relative px-5 py-5 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/130e61b16eaaa8ba903210754606a6e7.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/60 via-white/40 to-rose-50/60"></div>
        <div className="relative z-10">
          <p className="text-sm font-extrabold text-gray-800 mb-3 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-base"></i>
            主な特徴
          </p>
          <div className="space-y-3">
            {app.features.map((feature, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <i className="ri-check-line text-emerald-500 text-base mt-0.5 flex-shrink-0"></i>
                <span className="text-gray-800 text-sm font-bold leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 口コミ（横スワイプ） */}
      <ReviewCarousel reviews={app.reviews} />

      {/* 吹き出し＋インストールボタン */}
      <div className="px-5 pb-6">
        <div className="relative mb-4 flex justify-center">
          <div className="relative bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 inline-flex items-center gap-1.5">
            <i className="ri-trophy-fill text-amber-500 text-sm"></i>
            <span className="text-xs font-bold text-amber-700">{app.satisfactionBadge}</span>
            <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-amber-50 border-r border-b border-amber-200 rotate-45"></div>
          </div>
        </div>
        <a
          href={app.storeUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="relative block w-full text-center bg-gradient-to-r from-rose-500 to-pink-500 text-white py-5 rounded-full font-bold text-lg shadow-2xl whitespace-nowrap cursor-pointer transition-all duration-300 overflow-hidden"
          style={{
            transform: isHovered ? 'scale(1.05) translateY(-3px)' : 'scale(1) translateY(0)',
            boxShadow: isHovered
              ? '0 12px 35px rgba(244,63,94,0.6), 0 0 25px rgba(244,63,94,0.4)'
              : '0 6px 20px rgba(244,63,94,0.45)',
          }}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            <i className="ri-download-2-line text-2xl"></i>
            無料でインストール
            <i className="ri-arrow-right-line text-xl"></i>
          </span>
          {isHovered && (
            <span className="absolute inset-0 bg-gradient-to-r from-pink-400 to-rose-400 animate-pulse"></span>
          )}
        </a>
      </div>
    </div>
  );
}

export default function AppRecommendSection() {
  return (
    <section id="apps" className="py-10 px-4 bg-gradient-to-b from-rose-50/50 to-white">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block bg-rose-100 text-rose-500 text-xs font-bold px-4 py-1.5 rounded-full mb-3">
            RECOMMEND
          </span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">おすすめマッチングアプリ</h2>
          <p className="text-gray-500 text-xs leading-relaxed">
            編集部が厳選した人気アプリをご紹介
          </p>
        </div>

        {/* ランキング比較表 */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-4 py-3 text-center">
            <h3 className="text-white font-bold text-base flex items-center justify-center gap-2">
              <i className="ri-trophy-fill text-amber-300"></i>
              ランキング比較表
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-rose-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-left font-bold text-gray-700">アプリ名</th>
                  <th className="px-3 py-3 text-left font-bold text-gray-700">特徴</th>
                  <th className="px-3 py-3 text-center font-bold text-gray-700">評価</th>
                  <th className="px-3 py-3 text-center font-bold text-gray-700 whitespace-nowrap">DL数</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, index) => (
                  <tr key={app.id} className="border-b border-gray-100 hover:bg-rose-50/30 transition-colors">
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="font-bold text-gray-800 text-xs whitespace-nowrap">{app.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-gray-600 text-[11px] leading-relaxed">
                      {app.recommendPoint}
                    </td>
                    <td className="px-3 py-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`ri-star-fill text-[10px] ${
                                i < Math.floor(app.rating) ? 'text-amber-400' : 'text-gray-200'
                              }`}
                            ></i>
                          ))}
                        </div>
                        <span className="text-[11px] font-bold text-gray-700">{app.rating}</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-center font-bold text-gray-700 whitespace-nowrap">
                      {app.downloads}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          {apps.map((app, index) => (
            <div key={app.id}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 flex items-center justify-center bg-rose-500 text-white text-xs font-bold rounded-full">
                  {index + 1}
                </span>
                <span className="text-sm font-bold text-gray-700">第{index + 1}位</span>
              </div>
              <AppCard app={app} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}